import React, { useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Header from "../components/Header";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../backend/firebase";

import Login from "../screens/login-screen/Login";
import Home from "../screens/home-screen/Home";
import Profile from "../screens/profile-screen/Profile";
import AddDataset from "../screens/add-dataset/AddDataset";
import DatasetExpand from "../screens/dataset-expand/DatasetExpand";
import UpdateDataset from "../screens/update-dataset/UpdateDataset";
import UpdateDetails from "../screens/update-details/UpdateDetails";
import DatasetAccess from "../screens/dataset-access/DatasetAccess";
import DatasetRequests from "../screens/dataset-requests/DatasetRequests";
import Search from "../screens/search/Search";
import SearchResults from "../screens/search-results/SearchResults";
import ProfileActions from "../screens/profile-actions/ProfileActions";
import LikedScreen from "../screens/liked-screen/LikedScreen";
import DownloadScreen from "../screens/download-screen/DownloadScreen";
import CollectionsScreen from "../screens/collections-screen/CollectionsScreen";
import AddCollection from "../screens/add-collection/AddCollection";
import PublicCollection from "../screens/public-collection/PublicCollection";
import PrivateCollection from "../screens/private-collection/PrivateCollection";
import CSVCanvas from '../screens/csv-canvas/CSVCanvas'
import SetaDocs from "../screens/seta-documentation/SetaDocs";

import { ClipLoader } from "react-spinners";

// ⭐ Heading Mapper (pure function)
function getHeading(pathname) {
  if (pathname.startsWith("/search-results")) return "Search Results";
  if (pathname.startsWith("/search")) return "Search";
  if (pathname.startsWith("/user")) return auth.currentUser?.displayName || "";

  if (pathname.startsWith("/dataset/")) return "Dataset";

  if (pathname.startsWith("/update/")) return "Update Dataset";
  if (pathname.startsWith("/details/")) return "Update Details";

  if (pathname.startsWith("/add/")) return "Add Dataset";

  if (pathname.startsWith("/access/")) return "Dataset Access";

  if (pathname.startsWith("/request/")) return "Requests";

  if (pathname.startsWith("/collections")) return "Collections";
  if (pathname.startsWith("/add-collection")) return "Add Collection";

  if (pathname.startsWith("/liked")) return "Liked Datasets";
  if (pathname.startsWith("/downloads")) return "Downloads";

  if (pathname.startsWith("/profile-actions")) return "Profile Actions";
  if (pathname.startsWith("/user/")) return "Profile";

  if (pathname.startsWith("/public-collection")) return "Public Collection";
  if (pathname.startsWith("/private-collection")) return "Private Collection";
  if (pathname.startsWith("/canvas")) return "canvas";
  if (pathname.startsWith("/documentation")) return "documentation";

  return "";
}

// ⭐ Loading Bar Trigger
function RouteChangeLoader({ progressRef }) {
  const location = useLocation();

  useEffect(() => {
    progressRef.current?.continuousStart();
    setTimeout(() => {
      progressRef.current?.complete();
    }, 400);
  }, [location]);

  return null;
}

// ⭐ Component inside Router (so it can use useLocation)
function PageLayout({ user, progressRef }) {
  const location = useLocation();
  const heading = getHeading(location.pathname);

  return (
    <>
      {/* Header with dynamic heading */}
      <Header heading={heading} hideSearch={!user} />

      {/* Loading bar below header */}
      <LoadingBar height={3} color="#667eea" ref={progressRef} />
      <RouteChangeLoader progressRef={progressRef} />

      {/* Main content with proper spacing */}
      <div style={{ paddingTop: 55 }}>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/" replace />}
          />

          <Route
            path="/"
            element={user ? <Home /> : <Navigate to="/login" replace />}
          />

          <Route
            path="/user/:id"
            element={user ? <Profile /> : <Navigate to="/" replace />}
          />

          <Route
            path="/add/:id"
            element={user ? <AddDataset /> : <Navigate to="/" replace />}
          />

          <Route
            path="/documentation"
            element={<SetaDocs />}
          />

          <Route
            path="/dataset/:datasetid"
            element={<DatasetExpand />}
          />

          <Route
            path="/update/:datasetid"
            element={user ? <UpdateDataset /> : <Navigate to="/" replace />}
          />

          <Route
            path="/details/:datasetid"
            element={user ? <UpdateDetails /> : <Navigate to="/" replace />}
          />

          <Route
            path="/access/:datasetid"
            element={user ? <DatasetAccess /> : <Navigate to="/" replace />}
          />

          <Route
            path="/request/:uid"
            element={user ? <DatasetRequests /> : <Navigate to="/" replace />}
          />

          <Route
            path="/search"
            element={user ? <Search /> : <Navigate to="/" replace />}
          />

          <Route
            path="/search-results"
            element={user ? <SearchResults /> : <Navigate to="/" replace />}
          />

          <Route
            path="/profile-actions/:uid"
            element={user ? <ProfileActions /> : <Navigate to="/" replace />}
          />

          <Route
            path="/liked/:uid"
            element={user ? <LikedScreen /> : <Navigate to="/" replace />}
          />

          <Route
            path="/downloads/:uid"
            element={user ? <DownloadScreen /> : <Navigate to="/" replace />}
          />
          <Route
            path="/canvas/:datasetid/:versionid"
            element={user ? <CSVCanvas /> : <Navigate to="/" replace />}
          />

          <Route
            path="/collections/:uid"
            element={user ? <CollectionsScreen /> : <Navigate to="/" replace />}
          />

          <Route
            path="/add-collection/:uid"
            element={user ? <AddCollection /> : <Navigate to="/" replace />}
          />

          <Route path="/public-collection/:id" element={<PublicCollection />} />

          <Route
            path="/private-collection/:id"
            element={user ? <PrivateCollection /> : <Navigate to="/" replace />}
          />
        </Routes>
      </div>
    </>
  );
}

// ⭐ MAIN COMPONENT
export default function RootStack() {
  const [user, loading] = useAuthState(auth);
  const progressRef = useRef(null);

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          backgroundColor: "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ClipLoader size={30} color={"grey"} />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "black",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Router>
        <PageLayout user={user} progressRef={progressRef} />
      </Router>
    </div>
  );
}
